import { Cross2Icon } from "@radix-ui/react-icons";
import { Table } from "@tanstack/react-table";
import Cookies from "js-cookie";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { AxiosError } from "axios";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select";
import { Lead } from "./schema";

import { Button } from "@/components/ui/button/button";
import { Input } from "@/components/ui/input/input";
import { DataTableViewOptions } from "./data-table-view-options";
import { takeActionOnLead } from "@/config/mutation/employee/lead";
import { Label } from "@/components/ui/label/label";

// import { DataTableFacetedFilter } from "./data-table-faceted-filter";

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
}

export function DataTableToolbar<TData>({
  table,
}: DataTableToolbarProps<TData>) {
  const isFiltered = table.getState().columnFilters.length > 0;
  const key = Cookies.get("key") as string;
  const queryClient = useQueryClient();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterSelection, setFilterSelection] = useState({
    fNumber: 0,
    fBroker: "",
  });

  const handleOnChangeFilterSelection = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;

    setFilterSelection((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleSelectionOfFilter = () => {
    const rowModel = table.getCoreRowModel().rows;
    const filtered = rowModel.filter(
      (row) =>
        (row.original as Lead)?.broker?.toLowerCase() ===
        filterSelection.fBroker?.toLowerCase()
    );

    let count = 0;

    for (const row of filtered) {
      if (row.getIsSelected()) {
        continue;
      }

      if (count < filterSelection.fNumber) {
        const originData = row.original as Lead;

        if (
          originData?.broker?.toLowerCase() ===
          filterSelection.fBroker?.toLowerCase()
        ) {
          row.toggleSelected(true);
          count++;
        }
      }
    }

    if (count === 0) {
      toast("There is nothing to select.");
    } else {
      setFilterSelection({
        fNumber: 0,
        fBroker: "",
      });

      setIsFilterOpen(false);
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: takeActionOnLead(key),
  });

  const handleAssignSelectedLeads = () => {
    const selectedRows = table.getSelectedRowModel().rows;
    const formatData = selectedRows?.map((row) => {
      const originData = row.original as {
        id: string;
        alloted: boolean;
      };

      return originData?.id;
    });

    mutate(
      {
        leads: formatData,
      },
      {
        onSuccess: async () => {
          // console.log("New Employee", data);
          await queryClient.invalidateQueries({
            queryKey: ["leads"],
          });

          table.toggleAllPageRowsSelected(false);
          toast.success("Leads assigned to you.");
        },

        onError: (error) => {
          console.log("ERROR WHILE ASSIGNING LEADS TO CURRENT EMPLOYEE", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex flex-1 items-center space-x-2">
        <Input
          placeholder="Filter by name..."
          value={(table.getColumn("name")?.getFilterValue() as string) ?? ""}
          onChange={(event) =>
            table.getColumn("name")?.setFilterValue(event.target.value)
          }
          className="h-8 w-[150px] lg:w-[250px]"
        />

        <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
          <PopoverTrigger>
            <Button
              variant="outline"
              size="sm"
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              Selection Filter
            </Button>
          </PopoverTrigger>
          <PopoverContent className="max-w-xl w-full">
            <div className="flex flex-col gap-4">
              <div className="flex flex-row items-center gap-3">
                <div className="flex flex-col gap-1">
                  <Label htmlFor="f_number">How many selection?</Label>
                  <Input
                    id="f_number"
                    name="fNumber"
                    type="number"
                    value={filterSelection.fNumber}
                    onChange={handleOnChangeFilterSelection}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor="f_broker">Which broker?</Label>
                  <Select
                    value={filterSelection.fBroker}
                    onValueChange={(value) => {
                      setFilterSelection((prev) => {
                        return {
                          ...prev,
                          fBroker: value,
                        };
                      });
                    }}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="select a broker" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ANGELONE">Angel One</SelectItem>
                      <SelectItem value="ICICI">ICICI</SelectItem>
                      <SelectItem value="UPSTOX">Upstox</SelectItem>
                      <SelectItem value="ALICEBLUE">AliceBlue</SelectItem>
                      <SelectItem value="ZERODHA">Zerodha</SelectItem>
                    </SelectContent>
                  </Select>
                  {/* <Input
                    id="f_broker"
                    name="fBroker"
                    type="text"
                    value={filterSelection.fBroker}
                    onChange={handleOnChangeFilterSelection}
                  /> */}
                </div>
              </div>

              <div className="flex items-center justify-end">
                <Button
                  size="sm"
                  onClick={handleSelectionOfFilter}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  Select
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* {table.getColumn("priority") && (
          <DataTableFacetedFilter
            column={table.getColumn("priority")}
            title="Priority"
            options={priorities}
          />
        )} */}

        {table.getSelectedRowModel().rows.length > 0 && (
          <Button
            disabled={isPending}
            aria-label="Assign selected leads"
            size="sm"
            className="h-8 self-end"
            type="button"
            onClick={handleAssignSelectedLeads}
          >
            Assign to me
          </Button>
        )}
        {isFiltered && (
          <Button
            variant="ghost"
            onClick={() => table.resetColumnFilters()}
            className="h-8 px-2 lg:px-3"
          >
            Reset
            <Cross2Icon className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>

      <DataTableViewOptions table={table} />
    </div>
  );
}
